import type { NetworkPoiExportItem } from '@/api/poisApi';
import { getLeakEntityStatusDefinition, getPoiEntityStatusDefinition } from '@/components/map/utils/entityStatus';
import type { LeakEntityStatus, PoiEntityStatus } from '@/components/map/utils/entityStatus';
import type { LeakVisibilityOption } from '@/components/map/utils/mapFilters';
import type { EntityExportRow } from './poiExport';

/** The Download panel's per-family status switches (the split entity vocabulary). */
export interface ExportStatusSelection {
    poiStatuses: Record<PoiEntityStatus, boolean>;
    leakStatuses: Record<LeakEntityStatus, boolean>;
}

/** The Download panel's record-level controls (applied to the backing record, not per entity). */
export interface ExportRecordFilters {
    searchedOnly: boolean;
    leakVisibility: LeakVisibilityOption;
    /** ISO YYYY-MM-DD bounds; either may be null for an open end. */
    dateFrom: string | null;
    dateTo: string | null;
}

// Record-level dimensions mirror the canonical filter CTE's row semantics (PoiFilterSql):
// leak visibility excludes nulls under both non-all options; a record is in a date range
// when EITHER its search or surveyed date qualifies, and a record with neither date is
// excluded whenever a bound is active. Searched is NOT record-level — it is a POI-entity
// dimension (a recorded leak is a leak whether or not its record carries a search date),
// applied at the POI emission below.
function recordPasses(item: NetworkPoiExportItem, filters: ExportRecordFilters): boolean {
    if (filters.leakVisibility !== 'all') {
        if (item.isLeakVisible !== (filters.leakVisibility === 'visible')) return false;
    }

    if (filters.dateFrom != null || filters.dateTo != null) {
        const inRange = (date: string | null): boolean =>
            date != null &&
            (filters.dateFrom == null || date >= filters.dateFrom) &&
            (filters.dateTo == null || date <= filters.dateTo);
        if (!inRange(item.searchDate) && !inRange(item.surveyedDate)) return false;
    }

    return true;
}

/**
 * Projects the fetched records into entity rows and applies the Download panel's
 * selection: record-level controls first, then each projected entity against its own
 * family's status switches — the same per-family independence the map and table use.
 * Wire statuses resolve through the registries, so an unknown value follows the shared
 * tolerance rule (presents and filters as Unclassified). Records from a pre-split API
 * carry no entity statuses and project nothing — consistent with the explicit degraded
 * state, the export never guesses.
 */
export function selectEntityExportRows(
    items: NetworkPoiExportItem[],
    selection: ExportStatusSelection,
    recordFilters: ExportRecordFilters,
): EntityExportRow[] {
    const rows: EntityExportRow[] = [];

    for (const item of items) {
        if (!recordPasses(item, recordFilters)) continue;

        if (item.poiEntityStatus != null && !(recordFilters.searchedOnly && item.searchDate == null)) {
            const status = getPoiEntityStatusDefinition(item.poiEntityStatus as PoiEntityStatus).status;
            if (selection.poiStatuses[status]) rows.push({ entityType: 'poi', item });
        }

        if (item.leakEntityStatus != null) {
            const status = getLeakEntityStatusDefinition(item.leakEntityStatus as LeakEntityStatus).status;
            if (selection.leakStatuses[status]) rows.push({ entityType: 'leak', item });
        }
    }

    return rows;
}
